import { useRef, createRef, useEffect, useContext, useState, useLayoutEffect } from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import { useSelector, shallowEqual } from 'react-redux'
import wpautop from 'wpautop'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { createUseStyles, useTheme } from 'react-jss'
import BtnLink from '@/components/BtnLink'
import SimpleMedia from '@/components/SimpleMedia'
import SimpleSplitText from '@/components/SimpleSplitText'
import useScrollTrigger from '@/hooks/useScrollTrigger'
import { Context } from '@/context'
import { Context as ScrollbarContext } from '@/context/scrollbar'
import { removeParagraph, splitAfterBr } from '@/utils/strings'
import style from './style'

gsap.registerPlugin(ScrollTrigger)

const useStyles = createUseStyles(style)

const Hero = ({
  aboveTheFold,
  align,
  back,
  background,
  borderTitle,
  className,
  color,
  fontSize,
  img,
  imgSize,
  label,
  link,
  onComplete,
  pretitle,
  render,
  rightLink,
  text,
  textInside,
  title,
  viewportHeight,
  img1,
  img2,
  showImage,
}) => {
  const { scrollElement } = useContext(ScrollbarContext)
  const { headerHeight } = useContext(Context)
  const { animationReady } = useScrollTrigger()
  const [imageHeight, setImageHeight] = useState(0)
  const classes = useStyles({ headerHeight, imageHeight })
  const trigger = useRef([])
  const $backdrop = useRef()
  const $pretitleWrapper = useRef()
  const $pretitle = useRef()
  const $titleWrapper = useRef()
  const $titles = useRef(splitAfterBr(removeParagraph(title)).map(() => createRef()))
  const $rightLink = useRef()
  const $imageWrapper = useRef()
  const $image = useRef()
  const $overlay = useRef()
  const $ctaWrapper = useRef()
  const $textWrapper = useRef()
  const $textWrapperMobile = useRef()
  const $line = useRef()
  const history = useHistory()
  const theme = useTheme()

  const { mediaQueryKey, firstLoad } = useSelector((state) => ({
    mediaQueryKey: state.mediaQuery.key,
    firstLoad: state.loading.firstLoad,
  }), shallowEqual)

  const fadeIn = (ref, duration = 1.2) => {
    gsap.to(ref, { opacity: 1, ease: 'power3.out', duration })
  }

  const fadeInIE11 = (ref, duration = 1.2) => {
    gsap.to(ref, { opacity: theme.detect.isIE11 ? 0.6 : 1, ease: 'power3.out', duration })
  }

  useEffect(() => {
    if (scrollElement) {
      if ($imageWrapper.current) {
        const bounds = $imageWrapper.current.getBoundingClientRect()
        setImageHeight(bounds.height.toFixed(0))
      }
    }
    if (theme.detect.isIE11) {
      setTimeout(() => {
        if ($imageWrapper.current) {
          const bounds = $imageWrapper.current.getBoundingClientRect()
          setImageHeight(bounds.height.toFixed(0))
        }
      }, 1000)
    }
  }, [scrollElement])

  useLayoutEffect(() => {
    if (aboveTheFold) {
      if ($backdrop.current && !imgSize === 'full') setTimeout(() => fadeIn($backdrop.current), firstLoad ? 500 : 100)
      if ($overlay.current) setTimeout(() => fadeInIE11($overlay.current), firstLoad ? 500 : 100)
      if (pretitle) setTimeout(() => $pretitle.current.animateIn(), firstLoad ? 1500 : 500)
      if ($image.current) setTimeout(() => $image.current.animateIn(), firstLoad ? 1000 : 300)
      if ($titles.current) setTimeout(() => $titles.current.map((t) => t.current?.animateIn()), firstLoad ? 1500 : 500)
      if (borderTitle) {
        setTimeout(() => {
          gsap.to($line.current, {
            scaleX: 1,
          })
        }, firstLoad ? 1800 : 800)
      }
      if ($textWrapper.current) setTimeout(() => fadeIn($textWrapper.current), firstLoad ? 1800 : 800)
      if ($rightLink.current) setTimeout(() => fadeIn($rightLink.current), firstLoad ? 1800 : 800)
      if ($textWrapperMobile.current) setTimeout(() => fadeIn($textWrapperMobile.current), firstLoad ? 1800 : 800)
      if (link || back) setTimeout(() => fadeIn($ctaWrapper.current), firstLoad ? 2000 : 1000)
      if (onComplete) setTimeout(() => onComplete(), firstLoad ? 2200 : 1200)
    }
    if (!aboveTheFold && animationReady) {
      if ($backdrop.current && imgSize !== 'full') {
        trigger.current.push(ScrollTrigger.create({
          trigger: $backdrop.current,
          onEnter: () => fadeIn($backdrop.current, 1),
        }))
      }
      if ($backdrop.current) {
        trigger.current.push(ScrollTrigger.create({
          trigger: $overlay.current,
          onEnter: () => fadeIn($overlay.current, 1),
        }))
      }
      if ($pretitle.current) {
        trigger.current.push(ScrollTrigger.create({
          trigger: $pretitleWrapper.current,
          onEnter: () => $pretitle.current.animateIn(),
        }))
      }
      if ($titles.current.length > 0) {
        trigger.current.push(ScrollTrigger.create({
          trigger: $titleWrapper.current,
          onEnter: () => $titles.current.map((t) => t.current.animateIn()),
        }))
      }
      if ($rightLink.current) {
        trigger.current.push(ScrollTrigger.create({
          trigger: $rightLink.current,
          onEnter: () => fadeIn($rightLink.current, 1),
        }))
      }
      if (borderTitle) {
        trigger.current.push(gsap.to($line.current, {
          scrollTrigger: {
            trigger: $line.current,
            scrub: true,
            start: 'top bottom',
            end: 'top center',
            once: false,
          },
          scaleX: 1,
          ease: 'none',
        }))
      }
      if ($textWrapper.current) {
        trigger.current.push(ScrollTrigger.create({
          trigger: $textWrapper.current,
          onEnter: () => fadeIn($textWrapper.current),
        }))
      }
      if ($textWrapperMobile.current) {
        trigger.current.push(ScrollTrigger.create({
          trigger: $textWrapperMobile.current,
          onEnter: () => fadeIn($textWrapperMobile.current),
        }))
      }
      if (img) {
        trigger.current.push(ScrollTrigger.create({
          trigger: $imageWrapper.current,
          onEnter: () => $image.current.animateIn(),
        }))
      }
      if (link) {
        trigger.current.push(ScrollTrigger.create({
          trigger: $ctaWrapper.current,
          onEnter: () => fadeIn($ctaWrapper.current),
        }))
      }
    }
    return () => {
      if (trigger.current.length > 0) trigger.current.map((tr) => tr.kill())
    }
  }, [animationReady])

  const renderPretitle = () => {
    return (
      <SimpleSplitText
        ref={$pretitle}
        text={pretitle}
        disableHover
        appear
      />
    )
  }

  const renderImage = () => {
    return (
      <SimpleMedia
        ref={$image}
        className={classes.media}
        src={img}
        alt={title}
        prepareAnimation
      />
    )
  }

  const renderTitle = () => {
    const splittedTitle = splitAfterBr(removeParagraph(title))
    return splittedTitle.map((t, i) => (
      <div
        key={i.toString()}
        className={classes.titleLine}
      >
        <SimpleSplitText
          ref={$titles.current[i]}
          text={t}
          disableHover
          appear
        />
        {(borderTitle && (i + 1) !== splittedTitle.length) && (
          <span
            ref={$line}
            className={classes.line}
          />
        )}
        {(img1 && i === 0 && mediaQueryKey > 1) && (
          <img
            src={img1}
            alt=""
            className={classNames({
              [classes.img]: true,
              [classes.img1]: true,
              image: true,
              [classes.entered]: showImage,
            })}
            data-scroll
            data-scroll-speed="1"
          />
        )}
        {(img2 && i === 2 && mediaQueryKey > 1) && (
          <img
            src={img2}
            alt=""
            className={classNames({
              [classes.img]: true,
              [classes.img2]: true,
              image: true,
              [classes.entered]: showImage,
            })}
            data-scroll
            data-scroll-speed="2"
          />
        )}
      </div>
    ))
  }

  const renderText = () => {
    return <div dangerouslySetInnerHTML={{ __html: wpautop(text) }} />
  }

  const renderCta = () => {
    return back ? (
      <BtnLink
        onClick={() => history.goBack()}
        label="Indietro"
        theme={background === 'dark' ? 'light' : 'dark'}
      />
    ) : (
      <BtnLink
        label={label || 'Scopri'}
        link={link}
        theme={background === 'dark' ? 'light' : 'dark'}
      />
    )
  }

  return (
    <>
      <div
        className={classNames({
          [classes.root]: true,
          [classes.backgroundDark]: background === 'dark',
          [classes.backgroundLight]: background === 'light',
          [classes.backgroundRed]: background === 'red',
          [classes.backgroundArt]: background === 'art',
          [classes.backgroundBlue]: background === 'blue',
          [classes.colorLight]: color === 'light',
          [classes.colorDark]: color === 'dark',
          [classes.colorOlive]: color === 'olive',
          [classes.fontSizeSm]: fontSize === 'sm',
          [classes.fontSizeMd]: fontSize === 'md',
          [classes.fontSizeMd2]: fontSize === 'md-2',
          [classes.fontSizeLg]: fontSize === 'lg',
          [classes.aboveTheFold]: aboveTheFold,
          [classes.backgroundImage]: imgSize === 'full',
          [classes.alignRight]: align === 'right',
          [classes.alignLeft]: align === 'left',
          [classes.textInside]: textInside,
          [classes.render]: render,
          [classes.notViewportHeight]: !viewportHeight,
          [className]: className,
          Hero__root: true,
        })}
      >
        <div
          ref={$backdrop}
          className={classes.backdrop}
        />
        {(imgSize === 'full' && img) && (
          <div ref={$imageWrapper} className={classes.mediaContainer}>
            {renderImage()}
            <div
              ref={$overlay}
              className={classes.overlay}
            />
          </div>
        )}
        <div className={classes.content}>
          {pretitle && (
            <div ref={$pretitleWrapper} className={classes.pretitle}>
              {renderPretitle()}
            </div>
          )}
          <div ref={$titleWrapper} className={`${classes.title} Hero__title`}>
            {renderTitle()}
            {(imgSize === 'crop' && img && !textInside) && (
              <div ref={$imageWrapper} className={classes.mediaCrop}>
                {renderImage()}
              </div>
            )}
            {rightLink && (
              <div ref={$rightLink} className={classes.rightLink}>
                {rightLink()}
              </div>
            )}
          </div>
          {text && (
            <div ref={$textWrapper} className={classes.text}>
              {renderText()}
            </div>
          )}
          {(link || back) && (
            <div ref={$ctaWrapper} className={`${classes.cta} Hero__backBtn`}>
              {renderCta()}
            </div>
          )}
          {(imgSize === 'crop' && img && textInside) && (
            <div ref={$imageWrapper} className={classes.mediaCrop}>
              {renderImage()}
            </div>
          )}
        </div>
      </div>
      {(textInside && mediaQueryKey <= 1) && (
        <div ref={$textWrapperMobile} className={classes.textMobile}>
          {renderText()}
        </div>
      )}
    </>
  )
}

Hero.propTypes = {
  aboveTheFold: PropTypes.bool,
  align: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf(['left', 'right']),
  ]),
  back: PropTypes.bool,
  background: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf(['dark', 'light', 'red', 'blue', 'art']),
  ]),
  borderTitle: PropTypes.bool,
  className: PropTypes.string,
  color: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf(['dark', 'light', 'green']),
  ]),
  fontSize: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf(['sm', 'md', 'md-2', 'lg']),
  ]),
  img: PropTypes.string,
  imgSize: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf(['full', 'crop']),
  ]),
  label: PropTypes.string,
  link: PropTypes.string,
  onComplete: PropTypes.func,
  pretitle: PropTypes.string,
  render: PropTypes.bool,
  text: PropTypes.string,
  textInside: PropTypes.bool,
  title: PropTypes.string.isRequired,
  viewportHeight: PropTypes.bool,
}

Hero.defaultProps = {
  aboveTheFold: false,
  align: 'left',
  back: false,
  background: 'dark',
  borderTitle: false,
  className: '',
  color: '',
  fontSize: 'md',
  img: '',
  imgSize: 'full',
  label: '',
  link: '',
  onComplete: () => {},
  pretitle: '',
  render: false,
  text: '',
  textInside: false,
  viewportHeight: true,
}

export default Hero
